import { normalizeGender } from "@/features/settings/profile/utils/gender";

/**
 * Keep in sync with harmony-monorepo
 * `apps/api/src/api/user/compute-persona-completeness.ts`.
 * The /me `gate` flags use that copy; this one drives the quest UI.
 */

export const PERSONA_QUESTS = [
  "gender",
  "fit",
  "region",
  "phone",
  "tryOn",
] as const;

export type PersonaQuestId = (typeof PERSONA_QUESTS)[number];

export interface PersonaFieldSource {
  gender?: string | object | null;
  age?: number | string | null;
  height?: number | string | null;
  size?: string | null;
  shoeSize?: number | string | null;
  region?: string | null;
  phoneNumber?: string | null;
}

export interface PersonaCompleteness {
  quests: Record<PersonaQuestId, boolean>;
  completedCount: number;
  total: number;
  percent: number;
  isComplete: boolean;
  firstIncomplete: PersonaQuestId | null;
}

const hasText = (value: unknown): boolean =>
  typeof value === "string" && value.trim().length > 0;

const hasPositiveNumber = (value: unknown): boolean => {
  if (typeof value === "number") return Number.isFinite(value) && value > 0;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0;
  }
  return false;
};

export const isPersonaPhoneComplete = (phone: unknown): boolean => {
  if (!hasText(phone)) return false;
  const digits = String(phone).replace(/\D/g, "");
  return digits.length >= 8;
};

export const isPersonaFitComplete = (source: PersonaFieldSource): boolean =>
  hasPositiveNumber(source.age) &&
  hasPositiveNumber(source.height) &&
  hasText(source.size) &&
  hasPositiveNumber(source.shoeSize);

export const computePersonaCompleteness = (
  source: PersonaFieldSource,
  photoCount: number
): PersonaCompleteness => {
  const quests: Record<PersonaQuestId, boolean> = {
    gender: hasText(normalizeGender(source.gender)),
    fit: isPersonaFitComplete(source),
    region: hasText(source.region),
    phone: isPersonaPhoneComplete(source.phoneNumber),
    tryOn: photoCount >= 1,
  };

  const completedCount = PERSONA_QUESTS.filter((id) => quests[id]).length;
  const firstIncomplete =
    PERSONA_QUESTS.find((id) => !quests[id]) ?? null;

  return {
    quests,
    completedCount,
    total: PERSONA_QUESTS.length,
    percent: Math.round((completedCount / PERSONA_QUESTS.length) * 100),
    isComplete: completedCount === PERSONA_QUESTS.length,
    firstIncomplete,
  };
};
